import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
  ResponsiveContainer, ReferenceLine, LabelList
} from 'recharts';
import { format, parseISO, subYears } from 'date-fns';
import { pmToAqi, getAqiCategory } from '../utils/airQualityUtils';

// County-wide statistics section (Lameck's exceedance + per-zip analysis).
// Graph 1 — Days of exceedance per year: how many days the county-wide daily
//   average PM2.5 rose above the health threshold, one bar per year.
// Graph 2 — PM levels by zip code: the average daily PM2.5 for each zip over the
//   selected period, so users can explore air quality by area.

const BAR_COLOR = '#3b82f6';
const PARTIAL_COLOR = '#93c5fd';

// Build the comparison periods for the chosen window mode.
//  - calendar: one bucket per calendar year (current year flagged partial)
//  - trailing: rolling 12-month windows counted back from the latest data date
const buildPeriods = (mode, minDate, maxDate) => {
  const end = parseISO(maxDate);
  const start = parseISO(minDate);

  if (mode === 'calendar') {
    const periods = [];
    for (let y = start.getFullYear(); y <= end.getFullYear(); y++) {
      periods.push({
        key: `${y}`,
        label: `${y}`,
        start: `${y}-01-01`,
        end: `${y}-12-31`,
        isPartial: y === end.getFullYear()
      });
    }
    return periods;
  }

  // trailing: up to 4 rolling 12-month windows ending at maxDate
  const periods = [];
  for (let k = 0; k < 4; k++) {
    const winEnd = k === 0 ? end : subYears(end, k);
    const winStart = subYears(end, k + 1);
    if (winEnd <= start) break;
    const s = format(winStart, 'yyyy-MM-dd');
    const e = format(winEnd, 'yyyy-MM-dd');
    periods.push({
      key: `t${k}`,
      label: `${format(winStart, 'MMM yyyy')}–${format(winEnd, 'MMM yyyy')}`,
      start: s,
      end: e,
      isPartial: false
    });
  }
  return periods.reverse();
};

// Summarize a daily series within a period.
const statsInPeriod = (series, period, threshold) => {
  const pts = series.filter(p => p.date >= period.start && p.date <= period.end);
  const exceedanceDays = pts.filter(p => p.average > threshold).length;
  const avg = pts.length ? pts.reduce((s, p) => s + p.average, 0) / pts.length : null;
  return { validDays: pts.length, exceedanceDays, avg };
};

const ExceedanceTooltip = ({ active, payload, threshold }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white p-2 border rounded shadow-lg text-xs">
      <p className="font-medium mb-1">{d.label}{d.isPartial ? ' (partial)' : ''}</p>
      <p>{d.exceedanceDays} exceedance {d.exceedanceDays === 1 ? 'day' : 'days'} (&gt; {threshold} µg/m³)</p>
      <p className="text-gray-500">{d.validDays} days measured</p>
    </div>
  );
};

const ZipTooltip = ({ active, payload, threshold }) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white p-2 border rounded shadow-lg text-xs">
      <p className="font-medium mb-1">{d.town} ({d.zip})</p>
      <p>Avg PM2.5: {d.avg?.toFixed(1)} µg/m³</p>
      <p className="text-gray-500">{d.exceedanceDays} exceedance days · {d.validDays} days measured</p>
    </div>
  );
};

const WINDOWS = [
  { key: 'calendar', label: 'Calendar year' },
  { key: 'trailing', label: 'Trailing 12 mo' }
];

const CountyStatistics = ({ bare = false, only = null }) => {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('calendar'); // 'calendar' | 'trailing'
  const [zipPeriodKey, setZipPeriodKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${process.env.REACT_APP_API_URL}/statistics/annual`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) { setPayload(data); setError(null); }
      } catch (e) {
        if (!cancelled) setError(`Failed to load statistics: ${e.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const threshold = payload?.thresholds?.exceedance ?? 34.1;

  const periods = useMemo(() => {
    if (!payload?.county?.length) return [];
    const dates = payload.county.map(p => p.date);
    return buildPeriods(mode, dates[0], payload.range?.endDate || dates[dates.length - 1]);
  }, [payload, mode]);

  // County exceedance-per-period bars.
  const exceedanceData = useMemo(
    () => periods.map(p => ({ ...p, ...statsInPeriod(payload?.county || [], p, threshold) })),
    [periods, payload, threshold]
  );

  // Default the zip period to the most recent one whenever periods change.
  useEffect(() => {
    if (periods.length) setZipPeriodKey(periods[periods.length - 1].key);
  }, [periods]);

  const activePeriod = periods.find(p => p.key === zipPeriodKey) || periods[periods.length - 1];

  // Per-zip average bars for the active period, ordered by average descending.
  const zipData = useMemo(() => {
    if (!payload?.byZip || !activePeriod) return [];
    return Object.entries(payload.byZip)
      .map(([zip, series]) => {
        const s = statsInPeriod(series, activePeriod, threshold);
        return {
          zip,
          town: payload.zipTowns?.[zip] || zip,
          avg: s.avg,
          exceedanceDays: s.exceedanceDays,
          validDays: s.validDays
        };
      })
      .filter(d => d.avg !== null && d.validDays > 0)
      .sort((a, b) => b.avg - a.avg);
  }, [payload, activePeriod, threshold]);

  return (
    <div className={bare ? '' : 'p-6 bg-white rounded-lg shadow-md'}>
      {!bare && <h2 className="font-semibold text-lg mb-1">County Statistics</h2>}

      {loading ? (
        <div className="h-[300px] flex justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      ) : (
        <>
          {/* Window toggle — segmented control (gray track, white thumb),
              lives inside the chart window above the graph. */}
          <div className="flex justify-end mb-2">
            <div className="inline-flex items-center gap-0.5 bg-gray-100 rounded-full p-0.5">
              {WINDOWS.map(w => (
                <button
                  key={w.key}
                  onClick={() => setMode(w.key)}
                  className={`px-2.5 py-1 text-xs rounded-full transition-colors ${
                    mode === w.key
                      ? 'bg-white text-gray-800 font-medium shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {w.label}
                </button>
              ))}
            </div>
          </div>

          {/* Graph 1 — Days of exceedance per year */}
          {(!only || only === 'exceedance') && (
          <div className="mt-1">
            <h3 className="font-medium text-sm">Days of exceedance per year</h3>
            <p className="text-xs text-gray-600 mb-2">
              Compare years to see whether unhealthy air days are becoming more or less frequent.
              Each bar counts the days the county-wide daily average PM2.5 exceeded {threshold} µg/m³,
              a threshold based on the EPA's 24-hour PM2.5 standard (35 µg/m³).
              {mode === 'calendar' && exceedanceData.some(d => d.isPartial) &&
                ' The current year (lighter bar) is still in progress.'}
            </p>
            <div className="h-[240px] bg-gray-50 p-3 rounded-lg">
              <ResponsiveContainer width="99%" height="100%">
                <BarChart data={exceedanceData} margin={{ top: 15, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false}
                    label={{ value: 'Days', angle: -90, position: 'insideLeft', fontSize: 11 }} />
                  <Tooltip content={<ExceedanceTooltip threshold={threshold} />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                  <Bar dataKey="exceedanceDays" isAnimationActive={false} radius={[3, 3, 0, 0]}>
                    <LabelList dataKey="exceedanceDays" position="top" fontSize={11} />
                    {exceedanceData.map((d) => (
                      <Cell key={d.key} fill={d.isPartial ? PARTIAL_COLOR : BAR_COLOR} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          )}

          {/* Graph 2 — PM levels by zip code */}
          {(!only || only === 'zip') && (
          <div className={only ? 'mt-2' : 'mt-6'}>
            <div className="flex justify-between items-center">
              <h3 className="font-medium text-sm">Average PM2.5 by zip code</h3>
              {periods.length > 1 && (
                <select
                  value={zipPeriodKey || ''}
                  onChange={e => setZipPeriodKey(e.target.value)}
                  className="text-sm border rounded px-2 py-1"
                >
                  {[...periods].reverse().map(p => (
                    <option key={p.key} value={p.key}>{p.label}{p.isPartial ? ' (partial)' : ''}</option>
                  ))}
                </select>
              )}
            </div>
            <p className="text-xs text-gray-600 mb-2">
              Compare average daily PM2.5 across St. Mary's County zip codes to see how air quality
              varies by community. Bars are colored by air quality category; the dashed line marks
              the {threshold} µg/m³ exceedance threshold. Differences between zip codes may reflect
              localized weather, traffic, wildfire smoke, or other environmental factors, so small
              differences should not be over-interpreted.
            </p>
            <div className="h-[244px] bg-gray-50 p-3 rounded-lg">
              <ResponsiveContainer width="99%" height="100%">
                <BarChart data={zipData} margin={{ top: 15, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="town" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={50} />
                  <YAxis tick={{ fontSize: 11 }}
                    label={{ value: 'PM2.5 (µg/m³)', angle: -90, position: 'insideLeft', fontSize: 11 }} />
                  <Tooltip content={<ZipTooltip threshold={threshold} />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                  <ReferenceLine y={threshold} stroke="#ef4444" strokeDasharray="4 4" />
                  <Bar dataKey="avg" isAnimationActive={false} radius={[3, 3, 0, 0]}>
                    <LabelList dataKey="avg" position="top" fontSize={10}
                      formatter={(v) => (typeof v === 'number' ? v.toFixed(1) : v)} />
                    {zipData.map((d) => (
                      <Cell key={d.zip} fill={getAqiCategory(pmToAqi(d.avg)).color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          )}
        </>
      )}
    </div>
  );
};

export default CountyStatistics;
